import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import {
  medusaApi,
  useCreateCartMutation,
  useGetCartQuery,
  useAddToCartMutation,
  useUpdateCartItemMutation,
  useRemoveFromCartMutation,
} from '../services/api';
import {
  setCartId,
  openCart,
  closeCart,
  toggleCart,
  clearCart,
} from '../store/cartSlice';
import { addNotification } from '../store/uiSlice';
import { fetchRuntimeConfig } from '../services/config';

/**
 * Extract HTTP status code from RTK Query / fetch errors.
 * Returns 0 for network errors (no response received).
 */
function getErrorStatus(error: unknown): number {
  const err = error as Record<string, unknown>;
  // RTK Query error shape: { status: number, data: ... }
  if (typeof err?.status === 'number') return err.status;
  // Nested: { error: { status: number } }
  const inner = err?.error as Record<string, unknown> | undefined;
  if (typeof inner?.status === 'number') return inner.status;
  // originalStatus from fetchBaseQuery
  if (typeof err?.originalStatus === 'number') return err.originalStatus;
  // Network error / no response
  return 0;
}

/**
 * Retry an async operation with exponential backoff.
 * Delays: 1s, 2s, 4s (for maxRetries=3).
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const status = getErrorStatus(error);
      // Only retry on 5xx or network errors (status 0)
      if (status !== 0 && (status < 500 || status >= 600)) {
        throw error;
      }
      if (attempt < maxRetries - 1) {
        const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
        console.warn(`[Cart] Retry ${attempt + 1}/${maxRetries} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  throw lastError;
}

/**
 * Cart ViewModel - Encapsulates cart business logic and state management
 * Follows MVVM pattern where this acts as the ViewModel layer
 *
 * Error handling policy:
 * - 404: Cart gone — recreate silently
 * - 409/400: Stale cart state — refetch cart
 * - 5xx/network: Retry with exponential backoff, never clear cart
 * - clearCart() is ONLY called after successful order completion (in checkout flow)
 */
export const useCartViewModel = () => {
  const dispatch = useDispatch();
  const { cartId, isOpen } = useSelector((state: RootState) => state.cart);

  // Validate runtime config on mount  (logs validation info)
  useEffect(() => {
    fetchRuntimeConfig()
      .then(config => {
        const regionId = config.medusa.regionId;
        if (regionId) {
          console.log('[Cart] Configured region ID:', regionId);
        } else {
          console.error('[Cart] No region ID found in runtime config');
        }
      })
      .catch(error => {
        console.error('[Cart] Failed to load runtime config:', error);
      });
  }, []);

  // API hooks
  const [createCart] = useCreateCartMutation();
  const [addToCart] = useAddToCartMutation();
  const [updateCartItem] = useUpdateCartItemMutation();
  const [removeFromCart] = useRemoveFromCartMutation();

  // Get cart data
  const {
    data: cartData,
    isLoading: isCartLoading,
    error: cartError,
    refetch: refetchCart,
  } = useGetCartQuery(cartId!, {
    skip: !cartId,
  });

  const cart = cartId ? cartData?.cart : undefined;

  /** Create a fresh cart with the configured region */
  const createFreshCart = useCallback(async () => {
    const config = await fetchRuntimeConfig();
    const regionId = config.medusa.regionId;
    if (!regionId) {
      throw new Error('No region configured. Please ensure runtime config is loaded with a valid region ID.');
    }
    const result = await createCart({ region_id: regionId }).unwrap();
    dispatch(setCartId(result.cart.id));
    return result.cart;
  }, [createCart, dispatch]);

  /**
   * Handle cart errors based on HTTP status code.
   * Returns a recovered cart if possible, undefined otherwise.
   */
  const handleCartError = useCallback(async (error: unknown) => {
    const status = getErrorStatus(error);
    console.warn(`[Cart] Handling error with status ${status}`);

    if (status === 404) {
      // Cart genuinely gone — recreate silently
      console.warn('[Cart] Cart not found (404), recreating...');
      dispatch(clearCart());
      try {
        return await createFreshCart();
      } catch (createError) {
        console.error('[Cart] Failed to recreate cart after 404:', createError);
        return undefined;
      }
    }

    if (status === 409 || status === 400) {
      // Stale/invalid cart state — refetch to get fresh state
      console.warn(`[Cart] Stale cart state (${status}), refetching...`);
      try {
        const result = await refetchCart();
        if (result.data?.cart) {
          return result.data.cart;
        }
      } catch (refetchError) {
        console.error('[Cart] Failed to refetch cart:', refetchError);
      }
      dispatch(addNotification({
        type: 'error',
        message: 'Cart state is out of sync. Please try again.',
      }));
      return undefined;
    }

    // 5xx / network errors — should have been retried already by withRetry
    // Show error notification but NEVER clear cart
    dispatch(addNotification({
      type: 'error',
      message: 'A temporary error occurred. Please try again.',
    }));
    return undefined;
  }, [dispatch, createFreshCart, refetchCart]);

  // Initialize cart if none exists
  const initializeCart = useCallback(async () => {
    if (!cartId) {
      try {
        console.log('[Cart] No cart ID, creating new cart...');
        return await createFreshCart();
      } catch (error) {
        console.error('[Cart] Failed to create cart:', error);
        dispatch(addNotification({
          type: 'error',
          message: 'Failed to create cart',
        }));
        throw error;
      }
    }

    // If cartId exists, refetch the cart to ensure we have fresh data
    if (cartId && refetchCart) {
      console.log('[Cart] Refetching cart data for cartId:', cartId);
      try {
        const result = await refetchCart();

        if (result.data?.cart) {
          return result.data.cart;
        }

        if (result.error || result.isError) {
          const status = getErrorStatus(result.error);

          if (status === 404) {
            // Cart genuinely gone — recreate
            console.warn('[Cart] Cart not found (404), recreating...');
            dispatch(clearCart());
            return await createFreshCart();
          }

          // For other errors, refetch failed but don't clear the cart
          console.warn('[Cart] Cart refetch returned error (status', status, '), attempting recovery...');
          dispatch(addNotification({
            type: 'error',
            message: 'Failed to load cart. Please try again.',
          }));
          return undefined;
        }

        // Unexpected state: no data and no error — try creating fresh cart
        console.warn('[Cart] Unexpected refetch state (no data, no error), creating new cart...');
        dispatch(clearCart());
        return await createFreshCart();
      } catch (error) {
        const status = getErrorStatus(error);
        if (status === 404) {
          // Cart gone — recreate
          dispatch(clearCart());
          try {
            return await createFreshCart();
          } catch (createError) {
            console.error('[Cart] Failed to create new cart:', createError);
            return undefined;
          }
        }
        // For 5xx/network errors, don't clear cart
        console.error('[Cart] Failed to refetch cart (status', status, '):', error);
        dispatch(addNotification({
          type: 'error',
          message: 'Failed to load cart. Please try again.',
        }));
        return undefined;
      }
    }

    return cart;
  }, [cartId, createFreshCart, dispatch, cart, refetchCart]);

  // Add item to cart
  const addItem = useCallback(async (variantId: string, quantity: number = 1) => {
    console.log('[Cart] Adding item to cart:', { variantId, quantity, cartId });

    try {
      const currentCart = await initializeCart();

      if (!currentCart) {
        console.error('[Cart] Failed to initialize cart');
        return;
      }

      const result = await withRetry(() =>
        addToCart({
          cartId: currentCart.id,
          variantId,
          quantity,
        }).unwrap()
      );

      console.log('[Cart] Item added successfully:', result);

      dispatch(addNotification({
        type: 'success',
        message: 'Item added to cart',
        duration: 3000,
      }));

      dispatch(openCart());
    } catch (error) {
      console.error('[Cart] Failed to add item to cart:', error);
      await handleCartError(error);
    }
  }, [initializeCart, addToCart, dispatch, cartId, handleCartError]);

  // Update item quantity
  const updateQuantity = useCallback(async (lineId: string, quantity: number) => {
    if (!cartId) return;

    try {
      await withRetry(() =>
        updateCartItem({
          cartId,
          lineId,
          quantity,
        }).unwrap()
      );
    } catch (error) {
      console.error('[Cart] Failed to update item quantity:', error);
      await handleCartError(error);
    }
  }, [cartId, updateCartItem, handleCartError]);

  // Remove item from cart
  const removeItem = useCallback(async (lineId: string) => {
    if (!cartId) return;

    try {
      await withRetry(() =>
        removeFromCart({
          cartId,
          lineId,
        }).unwrap()
      );

      dispatch(addNotification({
        type: 'success',
        message: 'Item removed from cart',
        duration: 2000,
      }));
    } catch (error) {
      console.error('[Cart] Failed to remove item:', error);
      await handleCartError(error);
    }
  }, [cartId, removeFromCart, dispatch, handleCartError]);

  // Cart UI actions
  const openCartDrawer = useCallback(() => {
    dispatch(openCart());
  }, [dispatch]);

  const closeCartDrawer = useCallback(() => {
    dispatch(closeCart());
  }, [dispatch]);

  const toggleCartDrawer = useCallback(() => {
    dispatch(toggleCart());
  }, [dispatch]);

  // Clear entire cart (only for explicit user action or after successful order)
  const clearEntireCart = useCallback(() => {
    dispatch(clearCart());
    dispatch(medusaApi.util.resetApiState());
    dispatch(addNotification({
      type: 'info',
      message: 'Cart cleared',
      duration: 2000,
    }));
  }, [dispatch]);

  // Calculate totals
  const totals = {
    subtotal: cart?.subtotal || 0,
    total: cart?.total || 0,
    itemCount: cart?.items?.reduce((acc, item) => acc + item.quantity, 0) || 0,
    tax: cart?.tax_total || 0,
    shipping: cart?.shipping_total || 0,
  };

  return {
    // State
    cart,
    isOpen,
    isLoading: isCartLoading,
    error: cartError,
    totals,

    // Actions
    addItem,
    updateQuantity,
    removeItem,
    openCartDrawer,
    closeCartDrawer,
    toggleCartDrawer,
    clearEntireCart,
    initializeCart,
  };
};

// Export for testing
export { getErrorStatus, withRetry };
